/**
 * Whisky Library PostHog helper — safe to call before posthog.init() completes.
 * All theme and funnel scripts should use window.whiskyPosthog.*
 */
(function (window) {
  'use strict';

  var queue = [];
  var sdkReady = false;

  function getPosthog() {
    return typeof window.posthog !== 'undefined' ? window.posthog : null;
  }

  function isSdkReady() {
    var ph = getPosthog();
    return !!(ph && ph.__loaded);
  }

  function run(method, args) {
    var ph = getPosthog();
    if (!ph || typeof ph[method] !== 'function') return false;
    try {
      ph[method].apply(ph, args);
      return true;
    } catch (err) {
      return false;
    }
  }

  function enqueue(method, args) {
    queue.push({ method: method, args: args });
  }

  function flush() {
    if (!queue.length) return;
    var pending = queue.slice();
    queue.length = 0;
    pending.forEach(function (item) {
      run(item.method, item.args);
    });
  }

  function call(method, args) {
    if (isSdkReady()) {
      run(method, args);
      return;
    }
    enqueue(method, args);
  }

  function markSdkReady() {
    if (sdkReady) return;
    sdkReady = true;
    flush();
    // Re-dispatch here too: analytics-posthog.liquid's `loaded()` callback fires this same
    // event unconditionally, even if this script (loaded with `defer`) hasn't executed yet —
    // on a page where PostHog's own async init finishes before this deferred script runs,
    // that first dispatch fires into the void (window.whiskyPosthog doesn't exist yet, so
    // nothing is listening). Whichever of the two finishes last needs to be the one that
    // actually notifies waiting listeners; markSdkReady()'s one-time guard above makes this
    // safe to call unconditionally without double-firing.
    try {
      window.dispatchEvent(new CustomEvent('whisky-posthog-ready'));
    } catch (e) {
      // ignore
    }
    if (isDebug()) {
      console.info('[whiskyPosthog] SDK ready, queue flushed');
    }
  }

  function isDebug() {
    try {
      return new URLSearchParams(window.location.search).get('ph_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  function capture(event, properties) {
    if (!event) return;
    var props = properties || {};
    props.page_path = props.page_path || window.location.pathname;
    props.page_url = props.page_url || window.location.href;
    if (isDebug()) {
      console.info('[whiskyPosthog] capture', event, props);
    }
    call('capture', [event, props]);
  }

  function register(properties) {
    if (!properties) return;
    call('register', [properties]);
  }

  function registerOnce(properties) {
    if (!properties) return;
    call('register_once', [properties]);
  }

  function identify(distinctId, properties) {
    if (!distinctId) return;
    call('identify', [distinctId, properties || {}]);
  }

  function setPersonProperties(properties, setOnceProperties) {
    var setProps = properties && typeof properties === 'object' ? properties : null;
    var onceProps =
      setOnceProperties && typeof setOnceProperties === 'object' ? setOnceProperties : null;
    if (!setProps && !onceProps) return;
    call('setPersonProperties', [setProps || {}, onceProps || {}]);
  }

  function setPersonPropertiesForFlags(properties, reloadFeatureFlags) {
    if (!properties) return;
    if (typeof reloadFeatureFlags === 'boolean') {
      call('setPersonPropertiesForFlags', [properties, reloadFeatureFlags]);
      return;
    }
    call('setPersonPropertiesForFlags', [properties]);
  }

  var QUIZ_SESSION_ID_KEY = 'quiz_sessionId';
  var MILESTONE_USER_STORAGE_KEY = 'ph_milestone_user';
  /** Person property for 3-month-milestone release conditions (boolean). */
  var MILESTONE_USER_PROP = 'milestone-user';

  function hasQuizSessionId() {
    try {
      return !!(window.localStorage.getItem(QUIZ_SESSION_ID_KEY) || '').trim();
    } catch (e) {
      return false;
    }
  }

  function getStoredMilestoneUser() {
    try {
      var raw = window.localStorage.getItem(MILESTONE_USER_STORAGE_KEY);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch (e) {
      // ignore
    }
    return null;
  }

  /**
   * Decide once: milestone_user = true only if quiz_sessionId did not exist yet.
   * Returning quiz-takers (session already in localStorage) get false.
   * @returns {boolean}
   */
  function getOrCreateMilestoneUser() {
    var stored = getStoredMilestoneUser();
    if (stored !== null) return stored;

    // No prior quiz session → new milestone-experiment user.
    var isMilestoneUser = !hasQuizSessionId();
    try {
      window.localStorage.setItem(
        MILESTONE_USER_STORAGE_KEY,
        isMilestoneUser ? 'true' : 'false'
      );
    } catch (e) {
      // ignore storage errors
    }
    return isMilestoneUser;
  }

  /**
   * Record milestone_user once on the person (quiz landing) + for flag evaluation.
   * @returns {boolean}
   */
  function setMilestoneUserOnce() {
    var isMilestoneUser = getOrCreateMilestoneUser();
    var props = {};
    props[MILESTONE_USER_PROP] = isMilestoneUser;
    setPersonProperties({}, props);
    setPersonPropertiesForFlags(props, false);
    if (isDebug()) {
      console.info('[whiskyPosthog] set_once milestone_user', isMilestoneUser, {
        had_quiz_session: hasQuizSessionId()
      });
    }
    return isMilestoneUser;
  }

  /**
   * Re-send stored milestone_user for feature-flag /decide (taste profile).
   * Caller must reloadFeatureFlags() and read the variant inside onFeatureFlags.
   * @returns {boolean|null}
   */
  function applyMilestoneUserForFlags() {
    var stored = getStoredMilestoneUser();
    if (stored === null) return null;
    var props = {};
    props[MILESTONE_USER_PROP] = stored;
    setPersonProperties({}, props);
    setPersonPropertiesForFlags(props, false);
    if (isDebug()) {
      console.info('[whiskyPosthog] apply milestone_user for flags', stored);
    }
    return stored;
  }

  // Back-compat aliases (older call sites / cached bundles).
  function setFirstSeenAtOnce() {
    return setMilestoneUserOnce();
  }

  function applyFirstSeenAtForFlags() {
    return applyMilestoneUserForFlags();
  }

  function isFeatureEnabled(flagKey) {
    if (!flagKey) return false;
    var ph = getPosthog();
    if (!ph || typeof ph.isFeatureEnabled !== 'function') return false;
    try {
      return !!ph.isFeatureEnabled(flagKey);
    } catch (err) {
      return false;
    }
  }

  function getFeatureFlag(flagKey) {
    if (!flagKey) return undefined;
    var ph = getPosthog();
    if (!ph || typeof ph.getFeatureFlag !== 'function') return undefined;
    try {
      return ph.getFeatureFlag(flagKey);
    } catch (err) {
      return undefined;
    }
  }

  function onFeatureFlags(callback) {
    if (typeof callback !== 'function') return;

    function register() {
      var ph = getPosthog();
      if (!ph || typeof ph.onFeatureFlags !== 'function') {
        return;
      }
      try {
        ph.onFeatureFlags(callback);
      } catch (err) {
        callback();
      }
    }

    if (isSdkReady()) {
      register();
      return;
    }

    window.addEventListener('whisky-posthog-ready', register, { once: true });
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (isSdkReady()) {
        clearInterval(timer);
        register();
      } else if (attempts >= 120) {
        clearInterval(timer);
        callback();
      }
    }, 100);
  }

  function reloadFeatureFlags() {
    call('reloadFeatureFlags', []);
  }

  /** PostHog flag key — multivariate `test` (or boolean true) enables 3-month-milestone UI. */
  var FEATURE_FLAG_3_MONTH_MILESTONE = '3-month-milestone';

  function is3MonthMilestoneEnabled() {
    var flag = getFeatureFlag(FEATURE_FLAG_3_MONTH_MILESTONE);
    return flag === 'test' || flag === true;
  }

  /** null/undefined = flag hasn't loaded a value yet at all — not a real "control" answer. */
  function isMilestoneFlagResolved() {
    var flag = getFeatureFlag(FEATURE_FLAG_3_MONTH_MILESTONE);
    return flag !== undefined && flag !== null;
  }

  /**
   * Apply milestone_user, reload flags, then check variant only inside onFeatureFlags.
   * Avoids the race where the flag is read from cache before milestone_user is in /decide.
   * control / unmatched → existing UI; test → 3-month-milestone UI.
   * Shared by the quiz-hero section and the taste-profile results page so both read the
   * same flag the same way.
   *
   * The returned promise is bounded by `timeoutMs` so a render decision is never blocked
   * indefinitely. But a slow /decide can still resolve genuinely *after* that timeout fires —
   * `onResolved`, if given, is called every time a real (non-null) value is observed,
   * independent of whether the promise itself already settled, so a caller that wants to
   * durably remember the answer (e.g. cache it) doesn't lose a late-arriving correct value
   * just because the bounded promise already gave up and resolved to the timeout fallback.
   * @param {number} [timeoutMs]
   * @param {(enabled: boolean) => void} [onResolved]
   * @returns {Promise<boolean>}
   */
  function waitFor3MonthMilestoneFlag(timeoutMs, onResolved) {
    var wait = typeof timeoutMs === 'number' ? timeoutMs : 4000;
    return new Promise(function (resolve) {
      var settled = false;
      function finish(enabled) {
        if (settled) return;
        settled = true;
        resolve(!!enabled);
      }

      function requestFreshFlags() {
        applyMilestoneUserForFlags();

        var reloadRequested = false;
        onFeatureFlags(function () {
          if (!reloadRequested) return;
          // A stale/in-flight flags-loaded event (e.g. the automatic init /decide that ran
          // before milestone_user was set) can fire here with no value for this flag yet —
          // that's "not answered", not "control". Only act on a real value; otherwise keep
          // waiting for the next onFeatureFlags call (our own reload) or the timeout.
          if (!isMilestoneFlagResolved()) return;
          var enabled = is3MonthMilestoneEnabled();
          if (typeof onResolved === 'function') onResolved(enabled);
          finish(enabled);
        });

        reloadRequested = true;
        reloadFeatureFlags();
      }

      if (isSdkReady()) {
        requestFreshFlags();
      } else {
        window.addEventListener('whisky-posthog-ready', requestFreshFlags, { once: true });
      }

      window.setTimeout(function () {
        finish(is3MonthMilestoneEnabled());
      }, wait);
    });
  }

  /** Cached hero variant decision so a visitor never flips between reloads. */
  var MILESTONE_VARIANT_STORAGE_KEY = 'qh_milestone_variant';

  function getCachedMilestoneVariant() {
    try {
      var raw = window.localStorage.getItem(MILESTONE_VARIANT_STORAGE_KEY);
      if (raw === 'test' || raw === 'control') return raw;
    } catch (e) {
      // ignore
    }
    return null;
  }

  function setCachedMilestoneVariant(variant) {
    try {
      window.localStorage.setItem(MILESTONE_VARIANT_STORAGE_KEY, variant);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Resolve, once and durably, whether this visitor should see the 3-month-milestone hero
   * variant. The decision is driven purely by the cached `qh_milestone_variant` value once it
   * exists — never re-derived from `ph_milestone_user` or any other person-property, so it
   * can't disagree with itself across reloads.
   *
   * The cache is written ONLY from an actual PostHog flag answer — never fabricated:
   * - `quiz_sessionId` already exists → not a new visitor → control, but nothing is cached
   *   (no API call happened, so there's nothing genuine to persist).
   * - No `quiz_sessionId` yet → new visitor → ask PostHog for the "3-month-milestone" flag,
   *   via the `onResolved` hook so a real answer still gets cached even if it arrives after
   *   this call's own timeout already resolved the promise with the (unresolved) fallback.
   * @returns {Promise<boolean>}
   */
  function resolveMilestoneHeroVariant() {
    var cached = getCachedMilestoneVariant();
    if (cached) return Promise.resolve(cached === 'test');

    if (hasQuizSessionId()) {
      return Promise.resolve(false);
    }

    return waitFor3MonthMilestoneFlag(undefined, function (enabled) {
      setCachedMilestoneVariant(enabled ? 'test' : 'control');
    });
  }

  function getQuizSessionId() {
    try {
      return (window.localStorage.getItem('quiz_sessionId') || '').trim();
    } catch (e) {
      return '';
    }
  }

  function getQuizNavSource() {
    try {
      return sessionStorage.getItem('quiz_nav_source') || '';
    } catch (e) {
      return '';
    }
  }

  function getTasteProfileResult() {
    var root = document.querySelector('.taste-profile-page');
    if (!root) return '';
    return (root.getAttribute('data-taste-profile') || '').trim();
  }

  function funnelContext(extra) {
    var ctx = {
      quiz_session_id: getQuizSessionId(),
      quiz_nav_source: getQuizNavSource(),
      taste_profile_result: getTasteProfileResult(),
      shopify_template: window._template
        ? [window._template.directory, window._template.name, window._template.suffix].filter(Boolean).join('.')
        : undefined
    };
    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (key) {
        ctx[key] = extra[key];
      });
    }
    return ctx;
  }

  window.whiskyPosthog = {
    capture: capture,
    register: register,
    registerOnce: registerOnce,
    identify: identify,
    setPersonProperties: setPersonProperties,
    setPersonPropertiesForFlags: setPersonPropertiesForFlags,
    setMilestoneUserOnce: setMilestoneUserOnce,
    applyMilestoneUserForFlags: applyMilestoneUserForFlags,
    getStoredMilestoneUser: getStoredMilestoneUser,
    setFirstSeenAtOnce: setFirstSeenAtOnce,
    applyFirstSeenAtForFlags: applyFirstSeenAtForFlags,
    isFeatureEnabled: isFeatureEnabled,
    getFeatureFlag: getFeatureFlag,
    onFeatureFlags: onFeatureFlags,
    reloadFeatureFlags: reloadFeatureFlags,
    is3MonthMilestoneEnabled: is3MonthMilestoneEnabled,
    waitFor3MonthMilestoneFlag: waitFor3MonthMilestoneFlag,
    resolveMilestoneHeroVariant: resolveMilestoneHeroVariant,
    funnelContext: funnelContext,
    getQuizSessionId: getQuizSessionId,
    getQuizNavSource: getQuizNavSource,
    getTasteProfileResult: getTasteProfileResult,
    flush: flush,
    markSdkReady: markSdkReady,
    isSdkReady: isSdkReady,
    isDebug: isDebug
  };

  function tryMarkReady() {
    if (isSdkReady()) {
      markSdkReady();
      return true;
    }
    return false;
  }

  var attempts = 0;
  var timer = setInterval(function () {
    attempts += 1;
    if (tryMarkReady() || attempts >= 150) clearInterval(timer);
  }, 100);

  document.addEventListener('DOMContentLoaded', tryMarkReady);
  window.addEventListener('whisky-posthog-ready', markSdkReady);
})(window);
